import type { Order as OrderType } from 'effect/Order';
import type { FormattableDate } from '../date/date';
import { dayjs } from '../date/date';
import { EArray, Order, pipe } from '../effect';

type Getter<TInput, TOutput> = (item: TInput) => TOutput;

/**
 * Applies sort direction to an Order
 */
const applySortDirection = <T>(order: OrderType<T>, direction: SortDirection): OrderType<T> =>
  direction === 'desc' ? Order.reverse(order) : order;

/**
 * Creates an Order for sorting by a numeric value
 */
export const byNumber = <T>(getter: Getter<T, number>) => Order.mapInput(Order.number, getter);

/**
 * Creates an Order for sorting by a string value
 */
export const byString = <T>(getter: Getter<T, string>) => Order.mapInput(Order.string, getter);

/**
 * Creates an Order for sorting by a boolean value
 */
export const byBoolean = <T>(getter: Getter<T, boolean>) => Order.mapInput(Order.boolean, getter);

/**
 * Creates an Order for sorting by a timestamp (date) value
 */
export const byTimestamp = <T>(getter: Getter<T, DateTimeISOString | FormattableDate>) =>
  Order.mapInput(Order.number, (i: T) => dayjs(getter(i)).valueOf());

/**
 * Creates a sort function that can be used to sort arrays
 */
export const createSorter =
  <T>(order: OrderType<T>) =>
  (data: T[]) =>
    pipe(data, EArray.sort(order));

/**
 * Creates a sort function that can be used to sort arrays by a date value
 *
 * @example
 * ```typescript
 * type Task = {
 *   id: string;
 *   createdAt: DateTimeISOString;
 *   dueDate: DateTimeISOString;
 * }
 *
 * const tasks: Task[] = [
 *   { id: '1', createdAt: '2024-03-20T10:00:00Z', dueDate: '2024-03-25T10:00:00Z' },
 *   { id: '2', createdAt: '2024-03-19T10:00:00Z', dueDate: '2024-03-24T10:00:00Z' },
 *   { id: '3', createdAt: '2024-03-21T10:00:00Z', dueDate: '2024-03-26T10:00:00Z' }
 * ];
 *
 * // Sort by creation date, newest first
 * const sortByCreatedAt = sortByDate({
 *   getter: (task) => task.createdAt,
 *   sortDirection: 'desc'
 * });
 * const sortedByCreatedAt = sortByCreatedAt(tasks);
 *
 * // Sort by due date, earliest first
 * const sortByDueDate = sortByDate({
 *   getter: (task) => task.dueDate,
 *   sortDirection: 'asc'
 * });
 * const sortedByDueDate = sortByDueDate(tasks);
 * ```
 */
export const sortByDate = <T>(opts: {
  getter: Getter<T, DateTimeISOString | FormattableDate>;
  sortDirection: SortDirection;
}) => {
  const order = byTimestamp(opts.getter);
  const finalOrder = applySortDirection(order, opts.sortDirection);
  return (data: T[]) => pipe(data, EArray.sort(finalOrder));
};

/**
 * Creates a sort function that can be used to sort arrays by a string value
 */
export const sortByString = <T>(opts: { getter: Getter<T, string>; sortDirection: SortDirection }) => {
  const order = byString(opts.getter);
  const finalOrder = applySortDirection(order, opts.sortDirection);
  return (data: T[]) => pipe(data, EArray.sort(finalOrder));
};

/**
 * Creates a sort function that can be used to sort arrays by a number value
 */
export const sortByNumber = <T>(opts: { getter: Getter<T, number>; sortDirection: SortDirection }) => {
  const order = byNumber(opts.getter);
  const finalOrder = applySortDirection(order, opts.sortDirection);
  return (data: T[]) => pipe(data, EArray.sort(finalOrder));
};
